import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    ChevronLeft, Type, UserCircle, Ticket,
    Flag, Users, Building2, MapPin, Calendar,
    Shield, Tag, Monitor, Eye, Clock, Hash,
    CheckCircle2, AlertCircle,
    X, Layout, MessageSquare
} from 'lucide-react';
import baseapi from '../../services/baseapi';

export default function ConfigurationDetails() {
    const [searchParams, setSearchParams] = useSearchParams();
    const configId = searchParams.get('id');
    const [config, setConfig] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [previewContent, setPreviewContent] = useState<{ code: string; title: string } | null>(null);

    useEffect(() => {
        const fetchDetails = async () => {
            if (!configId) return;
            setLoading(true);
            try {
                const response = await baseapi.get(`/event_trigger_configs/${configId}/`);
                if (response.data.status) {
                    setConfig(response.data.data);
                }
            } catch (error) {
                console.error("Error fetching config details:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchDetails();
    }, [configId]);

    const handleBack = () => {
        searchParams.delete('id');
        searchParams.set('tab', 'configuration');
        setSearchParams(searchParams);
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] text-gray-500">
                <div className="w-10 h-10 border-4 border-orange-500/20 border-t-orange-500 rounded-full animate-spin mb-4" />
                <p className="text-sm font-bold uppercase tracking-widest animate-pulse">Retrieving Configuration...</p>
            </div>
        );
    }

    if (!config) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] text-gray-500">
                <AlertCircle className="w-12 h-12 mb-4 text-rose-500/50" />
                <p className="text-lg font-bold text-gray-900 dark:text-white mb-2">Configuration Not Found</p>
                <button onClick={handleBack} className="text-orange-500 hover:underline flex items-center gap-2">
                    <ChevronLeft size={16} /> Back to List
                </button>
            </div>
        );
    }

    const safeParse = (str: any) => {
        if (!str) return null;
        if (typeof str === 'object') return str;
        try { return JSON.parse(str); } catch (e) { return null; }
    };

    const parentConfig = config.trigger_config || config;
    const ticketConfig = config.rundeskpro_ticket_config || {};
    const notificationConfigs = config.notification_configs || [];

    const requestor = safeParse(ticketConfig.ticket_requestor || config.ticket_requestor);
    const ticketType = safeParse(ticketConfig.ticket_type || config.ticket_type);
    const priority = safeParse(ticketConfig.ticket_priority || config.ticket_priority);
    const department = safeParse(ticketConfig.ticket_department || config.ticket_department);
    const company = safeParse(ticketConfig.ticket_company || config.ticket_company);
    const site = safeParse(ticketConfig.ticket_site || config.ticket_site);

    const includeClients = safeParse(ticketConfig.include_clients || config.include_clients) || [];
    const excludeClients = safeParse(ticketConfig.exclude_clients || config.exclude_clients) || [];
    const includeTags = safeParse(ticketConfig.include_tags || config.include_tags) || [];
    const excludeTags = safeParse(ticketConfig.exclude_tags || config.exclude_tags) || [];

    const InfoCard = ({ icon: Icon, label, value, colorClass = "text-orange-500 dark:text-orange-400" }: any) => (
        <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/50 rounded-lg p-2.5 flex flex-col gap-0.5 transition-all hover:bg-gray-50 dark:hover:bg-gray-800/60 shadow-sm dark:shadow-none group">
            <div className="flex items-center gap-1.5 text-[9px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-[0.2em]">
                <Icon size={10} className={colorClass} />
                {label}
            </div>
            <div className="text-[11px] font-bold text-gray-900 dark:text-gray-100 truncate group-hover:text-gray-950 dark:group-hover:text-white">
                {value || '-'}
            </div>
        </div>
    );

    const FilterSection = ({ title, icon: Icon, include = [], exclude = [], type }: any) => (
        <div className="space-y-2">
            <div className="flex items-center gap-2 text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest px-1">
                <Icon size={12} className="text-orange-500" />
                {title} Filters
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="bg-emerald-500/5 border border-emerald-500/20 dark:border-emerald-500/10 rounded-lg p-3">
                    <div className="text-[8px] font-black text-emerald-600 dark:text-emerald-500 uppercase tracking-widest mb-2 flex items-center justify-between">
                        Included {type}
                        <span className="bg-emerald-500/10 dark:bg-emerald-500/20 px-1.5 py-0.5 rounded-full text-emerald-600 dark:text-emerald-400 text-[9px]">{include.length}</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                        {include.length > 0 ? include.map((item: any) => (
                            <span key={item.id} className="px-2 py-0.5 bg-emerald-500/10 border border-emerald-500/20 text-emerald-700 dark:text-emerald-400 text-[9px] font-bold rounded-md uppercase tracking-wider">
                                {item.name || item.display_name}
                            </span>
                        )) : <span className="text-[9px] text-gray-400 dark:text-gray-600 font-medium italic">None</span>}
                    </div>
                </div>
                <div className="bg-rose-500/5 border border-rose-500/20 dark:border-rose-500/10 rounded-lg p-3">
                    <div className="text-[8px] font-black text-rose-600 dark:text-rose-500 uppercase tracking-widest mb-2 flex items-center justify-between">
                        Excluded {type}
                        <span className="bg-rose-500/10 dark:bg-rose-500/20 px-1.5 py-0.5 rounded-full text-rose-600 dark:text-rose-400 text-[9px]">{exclude.length}</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                        {exclude.length > 0 ? exclude.map((item: any) => (
                            <span key={item.id} className="px-2 py-0.5 bg-rose-500/10 border border-rose-500/20 text-rose-700 dark:text-rose-400 text-[9px] font-bold rounded-md uppercase tracking-wider">
                                {item.name || item.display_name}
                            </span>
                        )) : <span className="text-[9px] text-gray-400 dark:text-gray-600 font-medium italic">None</span>}
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="flex flex-col gap-4 animate-in fade-in slide-in-from-bottom-2 duration-500 max-w-7xl mx-auto pb-8">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white dark:bg-gray-950/40 p-4 rounded-xl border border-gray-200 dark:border-gray-800/80 backdrop-blur-xl shadow-sm dark:shadow-none">
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleBack}
                        className="p-2 bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700 rounded-lg text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-all shadow-sm hover:scale-105 active:scale-95"
                    >
                        <ChevronLeft size={18} />
                    </button>
                    <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                            <h1 className="text-lg font-black text-gray-900 dark:text-white tracking-tight uppercase">
                                {parentConfig.name || config.name || "UNNAMED RULE"}
                            </h1>
                            <div className="px-2 py-0.5 rounded-md text-[8px] font-black uppercase tracking-widest border bg-indigo-50 dark:bg-gray-800 text-indigo-700 dark:text-indigo-400 border-indigo-200 dark:border-gray-700">
                                {(parentConfig.event_type || config.event_type || '').replace(/_/g, ' ')}
                            </div>
                            <div className={`px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest border ${
                                (parentConfig.is_active ?? config.is_active ?? true)
                                    ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-500 border-emerald-500/20"
                                    : "bg-rose-500/10 text-rose-600 dark:text-rose-500 border-rose-500/20"
                                }`}>
                                {(parentConfig.is_active ?? config.is_active ?? true) ? "Live & Active" : "Paused / Inactive"}
                            </div>
                        </div>
                        <p className="text-gray-500 dark:text-gray-400 text-[10px] font-black tracking-[0.3em] uppercase mt-1 flex items-center gap-2">
                            <Hash size={10} className="text-orange-500" />
                            Configuration ID: {parentConfig.id || config.id}
                            <span className="text-gray-300 dark:text-gray-700">|</span>
                            <Calendar size={10} className="text-orange-500" />
                            Deployed: {new Date(parentConfig.created || config.created).toLocaleDateString()}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <div className="flex flex-col items-end px-3 border-r border-gray-200 dark:border-gray-800">
                        <span className="text-[8px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest">Global Status</span>
                        <span className={`text-[10px] font-bold ${(parentConfig.is_global ?? config.is_global ?? false) ? "text-cyan-600 dark:text-cyan-400" : "text-amber-600 dark:text-amber-400"} uppercase tracking-wide`}>
                            {(parentConfig.is_global ?? config.is_global ?? false) ? "Global Rule" : "Tenant Specific"}
                        </span>
                    </div>
                    <div className="p-2 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                        <Shield size={18} className="text-orange-500" />
                    </div>
                </div>
            </div>

            {/* Quick Metadata Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                <InfoCard icon={UserCircle} label="Primary Requestor" value={requestor?.name} />
                <InfoCard icon={Ticket} label="Ticket Type" value={ticketType?.name} colorClass="text-indigo-600 dark:text-indigo-400" />
                <InfoCard icon={Flag} label="Incident Priority" value={priority?.name} colorClass={
                    priority?.id === 'high' || priority?.id === 'critical' ? "text-rose-600 dark:text-rose-400" : "text-amber-600 dark:text-amber-400"
                } />
                <InfoCard icon={Users} label="Target Department" value={department?.name} colorClass="text-cyan-600 dark:text-cyan-400" />
                <InfoCard icon={Building2} label="Client Entity" value={company?.name} />
                <InfoCard icon={MapPin} label="Deployment Site" value={site?.name} colorClass="text-indigo-600 dark:text-indigo-400" />
                <InfoCard icon={CheckCircle2} label="Auto-Create Ticket" value={ticketConfig && Object.keys(ticketConfig).length > 0 ? "Yes" : "No"} colorClass="text-emerald-600 dark:text-emerald-400" />
                <InfoCard icon={Clock} label="Last Updated" value={new Date(parentConfig.updated || config.updated).toLocaleTimeString()} colorClass="text-cyan-600 dark:text-cyan-400" />
            </div>

            {/* Rule Name & Title Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 shadow-sm dark:shadow-xl">
                    <div className="flex items-center gap-2 text-[9px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-[0.2em] mb-2">
                        <Type size={12} className="text-orange-500" />
                        Rule Name
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-950/50 border border-gray-200 dark:border-gray-800 rounded-lg p-3 font-mono text-[11px] text-gray-800 dark:text-gray-200 border-l-2 border-l-orange-500">
                        {parentConfig.name || config.name || '-'}
                    </div>
                </div>
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 shadow-sm dark:shadow-xl">
                    <div className="flex items-center gap-2 text-[9px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-[0.2em] mb-2">
                        <Type size={12} className="text-orange-500" />
                        Dynamic Rule Title
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-950/50 border border-gray-200 dark:border-gray-800 rounded-lg p-3 font-mono text-[11px] text-gray-800 dark:text-gray-200 border-l-2 border-l-orange-500">
                        {ticketConfig.ticket_title || config.ticket_title || '-'}
                    </div>
                </div>
            </div>

            {/* Description Sections */}
            {(ticketConfig.ticket_description || config.ticket_description || ticketConfig.ticket_closure_description || config.ticket_closure_description) && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Incident Description */}
                    <div className="flex flex-col gap-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 overflow-hidden shadow-sm dark:shadow-xl">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-[9px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-[0.2em]">
                                <Layout size={12} className="text-cyan-600 dark:text-cyan-400" />
                                Incident Description
                            </div>
                            <button
                                onClick={() => setPreviewContent({ code: ticketConfig.ticket_description || config.ticket_description || '', title: "Incident Description" })}
                                className="p-1 px-2 bg-cyan-600/10 border border-cyan-500/20 text-cyan-600 dark:text-cyan-400 rounded-md hover:bg-cyan-600/20 transition-all text-[9px] font-black uppercase tracking-widest flex items-center gap-1.5"
                            >
                                <Eye size={12} /> Preview
                            </button>
                        </div>
                        <div className="flex-1 bg-gray-50 dark:bg-gray-950/60 rounded-lg p-3 font-mono text-[10px] text-gray-700 dark:text-gray-400 overflow-y-auto max-h-[200px] custom-scroll border border-gray-200 dark:border-gray-800 shadow-inner">
                            <pre className="whitespace-pre-wrap">{ticketConfig.ticket_description || config.ticket_description || '-'}</pre>
                        </div>
                    </div>

                    {/* Closure Description */}
                    <div className="flex flex-col gap-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 overflow-hidden shadow-sm dark:shadow-xl">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-[9px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-[0.2em]">
                                <CheckCircle2 size={12} className="text-emerald-600 dark:text-emerald-400" />
                                Closure Description
                            </div>
                            <button
                                onClick={() => setPreviewContent({ code: ticketConfig.ticket_closure_description || config.ticket_closure_description || '', title: "Closure Description" })}
                                className="p-1 px-2 bg-emerald-600/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 rounded-md hover:bg-emerald-600/20 transition-all text-[9px] font-black uppercase tracking-widest flex items-center gap-1.5"
                            >
                                <Eye size={12} /> Preview
                            </button>
                        </div>
                        <div className="flex-1 bg-gray-50 dark:bg-gray-950/60 rounded-lg p-3 font-mono text-[10px] text-gray-700 dark:text-gray-400 overflow-y-auto max-h-[200px] custom-scroll border border-gray-200 dark:border-gray-800 shadow-inner">
                            <pre className="whitespace-pre-wrap">{ticketConfig.ticket_closure_description || config.ticket_closure_description || '-'}</pre>
                        </div>
                    </div>
                </div>
            )}

            {/* Active Notification Channels (Teams / Slack) */}
            {notificationConfigs.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {notificationConfigs.map((nc: any) => (
                        <div key={nc.integration_type} className="flex flex-col gap-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 overflow-hidden shadow-sm dark:shadow-xl">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 text-[9px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-[0.2em] font-bold">
                                    <MessageSquare size={12} className={nc.integration_type === 'slack' ? 'text-pink-500 dark:text-pink-400' : 'text-indigo-600 dark:text-indigo-400'} />
                                    {nc.integration_type.toUpperCase()} Integration
                                </div>
                                <div className={`px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest border ${nc.is_active
                                    ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-500 border-emerald-500/20"
                                    : "bg-rose-500/10 text-rose-600 dark:text-rose-500 border-rose-500/20"
                                    }`}>
                                    {nc.is_active ? "Active" : "Disabled"}
                                </div>
                            </div>
                            <div className="grid grid-cols-1 gap-2.5">
                                <InfoCard icon={Hash} label="Channel" value={nc.channel_name || nc.channel_id} colorClass="text-orange-500 dark:text-orange-400" />
                                <div className="space-y-1">
                                    <div className="text-[8px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest">Open Message</div>
                                    <div className="bg-gray-50 dark:bg-gray-950/60 rounded-lg p-3 font-mono text-[10px] text-gray-700 dark:text-gray-400 max-h-[120px] overflow-y-auto custom-scroll border border-gray-200 dark:border-gray-800">
                                        <pre className="whitespace-pre-wrap">{nc.channel_open_message || '-'}</pre>
                                    </div>
                                </div>
                                <div className="space-y-1 mt-2">
                                    <div className="text-[8px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest">Close Message</div>
                                    <div className="bg-gray-50 dark:bg-gray-950/60 rounded-lg p-3 font-mono text-[10px] text-gray-700 dark:text-gray-400 max-h-[120px] overflow-y-auto custom-scroll border border-gray-200 dark:border-gray-800">
                                        <pre className="whitespace-pre-wrap">{nc.channel_close_message || '-'}</pre>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Filter Sets */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FilterSection title="Client" icon={Users} include={includeClients} exclude={excludeClients} type="Clients" />
                <FilterSection title="Tag" icon={Tag} include={includeTags} exclude={excludeTags} type="Tags" />
            </div>

            {/* Preview Modal */}
            {previewContent && (
                <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 sm:p-8 animate-in fade-in duration-200">
                    <div className="fixed inset-0 bg-black/60 dark:bg-black/90 backdrop-blur-md" onClick={() => setPreviewContent(null)} />
                    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/10 rounded-2xl shadow-2xl dark:shadow-[0_0_80px_rgba(0,0,0,0.8)] w-full max-w-5xl h-[85vh] relative flex flex-col overflow-hidden zoom-in-95 z-10">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
                            <div className="flex items-center gap-3">
                                <div className="bg-orange-500/10 dark:bg-orange-500/20 p-2 rounded-xl text-orange-500 dark:text-orange-400">
                                    <Monitor size={20} />
                                </div>
                                <div>
                                    <h3 className="text-lg font-black text-gray-900 dark:text-white uppercase tracking-tight">Live Render: {previewContent.title}</h3>
                                    <p className="text-[10px] text-gray-500 dark:text-gray-400 font-black uppercase tracking-widest">High Fidelity Sandboxed Sandbox</p>
                                </div>
                            </div>
                            <button onClick={() => setPreviewContent(null)} className="p-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white rounded-xl border border-gray-200 dark:border-gray-700 transition-all">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="flex-1 p-6 bg-gray-100 dark:bg-gray-950">
                            <div className="w-full h-full bg-white rounded-xl overflow-hidden shadow-2xl ring-1 ring-gray-200 dark:ring-white/10">
                                <iframe
                                    title="Detail Preview"
                                    srcDoc={previewContent.code}
                                    className="w-full h-full border-none"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
